import { useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import {
  CalendarDateOutline,
  ClipboardText,
  CollapseAll,
  ExpandAll,
  Plus,
  Refresh,
} from '../icons';
import { Button, Tooltip, WorktimeTab } from '../Ant';
import CalendarView from '../task/calendar';
import WorktimeCalendar from './worktimeCalendar';
import AgendaView from './agendaView';
import {
  ICreateWorkTimeDto,
  IGroupedWorkTimes,
  IWorkTime,
  WorkTimeTab,
} from '../../interface/workTimes';
import { useTranslation } from 'react-i18next';
import { usePage } from '../../context/PageContext';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { ROUTES } from '../../constants/constants';
import { useFetchWorkTimeList } from '../../hooks/useFetchWorkTimeList';
import { useFetchAllPages } from '../../hooks/useFetchAllPages';
import { useUser } from '../../context/UserContext';
import { groupWorkTimesForDailyView, groupWorkTimesForWeeklyView } from '../../constants/workTime';
import { useWorkTime } from '../../context/WorkTimeContext';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateWorktime } from '../../hooks/useCreateWorktime';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsPage } from '../../interface/analytics';
import { useLocation, useSearchParams } from 'react-router-dom';
import { IActiveSortState, ISortOption } from '../../interface/common';
import {
  DateGroupList,
  toDateGroupKey,
  useCollapsedDateGroups,
} from '../../hooks/useCollapsedDateGroups';

dayjs.extend(isoWeek);
const WorkTimeTabView = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { setTitle } = usePage();
  const { user } = useUser();
  const { push, clear, updateTop } = useNavigationStack();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFromDate = searchParams.get('worktimeFromDate');
  const initialSelectedDate = searchParams.get('worktimeSelectedDate');
  const { updateWorkTime } = useWorkTime();
  const { trackPage } = useAnalytics();
  const fromTab = location?.state?.fromTab ?? WorkTimeTab.calendar;
  const [activeTab, setActiveTab] = useState<string>(fromTab);
  const startWeek = (d?: Date | null) => dayjs(d).utc().startOf('isoWeek').format('YYYY-MM-DD');
  const createWorkTimeMutation = useCreateWorktime();
  let initialFromDateValue: Date;
  if (initialFromDate) {
    initialFromDateValue = new Date(initialFromDate);
  } else if (initialSelectedDate) {
    initialFromDateValue = new Date(dayjs(initialSelectedDate).startOf('isoWeek').toISOString());
  } else {
    initialFromDateValue = new Date(startWeek());
  }
  const [fromDate, setFromDate] = useState<Date>(initialFromDateValue);
  const initialSelectedDateValue = initialSelectedDate ? new Date(initialSelectedDate) : new Date();
  const [selectedDate, setSelectedDate] = useState<Date>(initialSelectedDateValue);
  const [tempWorkTimes, setTempWorkTimes] = useState<IWorkTime[]>([]);
  const { getToggleAllControl } = useCollapsedDateGroups(DateGroupList.worktimeAgenda);
  const calendarActiveSort: IActiveSortState = { field: 'startTime', direction: 'ASC' };
  const calendarSortOptions: ISortOption[] = useMemo(
    () => [
      { field: 'startTime', label: t('worktime:startTime'), type: 'date', defaultDirection: 'ASC' },
    ],
    [t],
  );
  const {
    data: paginatedWorkTimesData,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isFetchNextPageError,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useFetchWorkTimeList({
    pageSize: 50,
    extraFilters: {
      workTimeUserId: user?.id,
      startTime: `$lte:${dayjs(fromDate).add(6, 'days').endOf('day').subtract(1, 'millisecond').toISOString()}`,
      endTime: `$gte:${dayjs(fromDate).startOf('day').toISOString()}`,
    },
    relations: ['workTimeType', 'matter:subject,matterNumber'],
    fields: ['startTime', 'endTime', 'workTimeUserId', 'duration', 'subject'],
  });

  useEffect(() => {
    setSearchParams({
      worktimeFromDate: dayjs(fromDate).toISOString(),
      worktimeSelectedDate: dayjs(selectedDate).toISOString(),
    });
  }, [fromDate, selectedDate]);

  useEffect(() => {
    trackPage(AnalyticsPage.WORK_TIME_LISTING);
  }, [trackPage]);

  useEffect(() => {
    updateTop({ fromTab: activeTab });
  }, [activeTab]);
  const groupedWorkTimes: IGroupedWorkTimes[] | undefined = useMemo(() => {
    if (isFetching && isFetchingNextPage) return;
    if (!paginatedWorkTimesData?.length) return;

    if (activeTab === WorkTimeTab.calendar) {
      return groupWorkTimesForDailyView([...paginatedWorkTimesData, ...tempWorkTimes], true);
    } else {
      return groupWorkTimesForWeeklyView(paginatedWorkTimesData, fromDate);
    }
  }, [paginatedWorkTimesData, activeTab, tempWorkTimes]);
  useFetchAllPages({ hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage });
  const tabItems = [
    {
      key: WorkTimeTab.calendar,
      label: (
        <span role="img" aria-label={t('task:tooltip.dayView')}>
          <CalendarDateOutline />
        </span>
      ),
    },
    {
      key: WorkTimeTab.agenda,
      label: (
        <span role="img" aria-label={t('common:agenda')}>
          <ClipboardText />
        </span>
      ),
    },
  ];

  const dailyAgendaProps = useMemo(
    () => ({
      selectedDate,
      workTimes:
        groupedWorkTimes?.find((groupedWorkTime) =>
          dayjs(groupedWorkTime.date).isSame(selectedDate, 'day'),
        )?.workTimes ?? [],
    }),
    [selectedDate, groupedWorkTimes, isFetching],
  );

  const workTimesWeeklyViewProps = useMemo(
    () => ({
      groupedWorkTimes: groupedWorkTimes ?? [],
      isLoading,
      isFetching,
      selectedDate,
      calendarActiveSort,
      calendarSortOptions,
    }),
    [groupedWorkTimes, isLoading, isFetching, selectedDate],
  );
  // Every page is fetched eagerly above, so this only waits out the initial load. Lists that page
  // on scroll offer the toggle over the groups loaded so far, as waiting would hide it for good.
  const isAgendaListReady = activeTab === WorkTimeTab.agenda && !hasNextPage && !isLoading;
  const dateGroupToggle = isAgendaListReady
    ? getToggleAllControl((groupedWorkTimes ?? []).map((group) => toDateGroupKey(group.date)))
    : undefined;
  const toggleAllLabel = t(
    dateGroupToggle?.areAllExpanded ? 'common:collapseAll' : 'common:expandAll',
  );
  const handleAddWorkTime = () => {
    updateWorkTime({
      startTime: dayjs(selectedDate).startOf('day').toISOString(),
      endTime: dayjs(selectedDate).endOf('day').toISOString(),
    } as unknown as IWorkTime);
    push(ROUTES.ADD_EDIT_WORK_TIME);
  };
  const refreshWorkTimeList = () => {
    queryClient.removeQueries({
      queryKey: ['worktime-list'],
    });
    refetch();
  };
  const handleCreateWorkTime = (workTime: IWorkTime) => {
    const basePayload: ICreateWorkTimeDto = {
      subject: workTime?.subject ?? '',
      startTime: workTime?.startTime ?? '',
      endTime: workTime?.endTime ?? '',
      workTimeUserId: user?.id ?? '',
    };
    setTempWorkTimes([...tempWorkTimes, basePayload as IWorkTime]);
    createWorkTimeMutation.mutate(basePayload, {
      onSuccess: () => {
        refetch().then(() => {
          setTempWorkTimes([]);
        });
      },
    });
  };
  const handleHome = () => {
    clear();
    push(ROUTES.DASHBOARD);
  };
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        title={t('common:mainHeader.worktimes')}
        showMenu
        showSettings
        handleHome={handleHome}
      />,
    );
  }, []);
  return (
    <Flex vertical gap={15} className="h-full">
      <CalendarView
        onWeekChange={(start) => {
          const selectedDayOfWeek = dayjs(selectedDate).day();
          setFromDate(start);
          setSelectedDate(
            dayjs(start)
              .isoWeekday(selectedDayOfWeek === 0 ? 7 : selectedDayOfWeek)
              .toDate(),
          );
        }}
        groupedMap={
          groupedWorkTimes
            ? groupedWorkTimes.reduce<Record<string, { items: IWorkTime[] }>>((acc, curr) => {
                const isToday = dayjs().isSame(curr?.date, 'day');
                const dateKey = isToday
                  ? t('common:today')
                  : dayjs(curr?.date).format('MMM DD, YYYY');

                acc[dateKey] = {
                  items: (curr.workTimes ?? []).map((wt) => wt.workTime),
                };
                return acc;
              }, {})
            : {}
        }
        onDateChange={(date) => {
          setSelectedDate(date);
        }}
        extracomponent={
          <Flex justify="space-between" align="center" className="mb-15">
            <WorktimeTab
              data-testid="worktime-tab"
              activeKey={activeTab}
              onChange={(key: string) => setActiveTab(key)}
              items={tabItems}
            />
            <Flex gap={5} align="center">
              <Tooltip title={t('common:refresh')} arrow={false}>
                <Button
                  data-testid="worktime-refresh-button"
                  aria-label={t('common:refresh')}
                  maxwidth="30px"
                  height="30px"
                  className="refresh-btn"
                  onClick={refreshWorkTimeList}
                >
                  <Refresh className="fs-14 flex-shrink-0" />
                </Button>
              </Tooltip>
              {dateGroupToggle && (
                <Tooltip title={toggleAllLabel} arrow={false}>
                  <Button
                    data-testid="worktime-toggle-all-date-groups-button"
                    aria-label={toggleAllLabel}
                    maxwidth="30px"
                    height="30px"
                    className="refresh-btn"
                    onClick={dateGroupToggle.onToggleAll}
                  >
                    {dateGroupToggle.areAllExpanded ? (
                      <ExpandAll className="fs-14 flex-shrink-0" />
                    ) : (
                      <CollapseAll className="fs-14 flex-shrink-0" />
                    )}
                  </Button>
                </Tooltip>
              )}
              <Tooltip title={t('worktime:addWorkTime')} arrow={false}>
                <Button
                  data-testid="worktime-add-button"
                  aria-label={t('worktime:addWorkTime')}
                  type="primary"
                  maxwidth="30px"
                  height="30px"
                  onClick={handleAddWorkTime}
                >
                  <Plus className="fs-18 flex-shrink-0" />
                </Button>
              </Tooltip>
            </Flex>
          </Flex>
        }
        initialWeekStart={fromDate}
        customDate={selectedDate}
      />
      {!hasNextPage && !isLoading && (
        <>
          {activeTab === WorkTimeTab.calendar && (
            <WorktimeCalendar
              {...dailyAgendaProps}
              createWorkTime={handleCreateWorkTime}
              workTimes={dailyAgendaProps.workTimes.map((x) => x.workTime)}
            />
          )}
          {activeTab === WorkTimeTab.agenda && <AgendaView {...workTimesWeeklyViewProps} />}
        </>
      )}
    </Flex>
  );
};

export default WorkTimeTabView;
